// ============================================================================
// SellerDashboard.jsx — Seller sales analytics, order management, payouts
// Route: /seller (protected)
// ============================================================================

import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Link, useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || ''

const STATUS_COLORS = {
  paid: 'bg-blue-100 text-blue-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  disputed: 'bg-red-100 text-red-700',
}

export default function SellerDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('overview')
  const [sellerStatus, setSellerStatus] = useState(null)
  const [orders, setOrders] = useState([])
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?.email) return
    fetchAll()
  }, [user?.email])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [statusRes, ordersRes, listingsRes] = await Promise.all([
        fetch(`${API_URL}/sellers/status?email=${encodeURIComponent(user.email)}`),
        fetch(`${API_URL}/orders/my?email=${encodeURIComponent(user.email)}&role=seller`),
        fetch(`${API_URL}/marketplace/my-listings?email=${encodeURIComponent(user.email)}`)
      ])
      const [statusData, ordersData, listingsData] = await Promise.all([
        statusRes.json(), ordersRes.json(), listingsRes.json()
      ])
      setSellerStatus(statusData)
      setOrders(ordersData.orders || [])
      setListings(listingsData.listings || [])
    } catch (e) {
      console.error('Seller dashboard error:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleShip = async (orderId) => {
    const tracking = window.prompt('Enter tracking number (or leave blank):')
    if (tracking === null) return
    try {
      await fetch(`${API_URL}/orders/${orderId}/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': user.email },
        body: JSON.stringify({ tracking_number: tracking || null, carrier: tracking ? 'USPS' : null })
      })
      fetchAll()
    } catch (e) { console.error(e) }
  }

  const handleStripeDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/sellers/dashboard?email=${encodeURIComponent(user.email)}`)
      const data = await res.json()
      if (data.url) window.open(data.url, '_blank')
    } catch (e) { console.error(e) }
  }

  // Stats
  const totalRevenue = orders.filter(o => ['delivered', 'completed'].includes(o.status)).reduce((s, o) => s + (o.seller_payout_amount || 0), 0)
  const pendingOrders = orders.filter(o => ['paid', 'processing'].includes(o.status)).length
  const disputedOrders = orders.filter(o => o.status === 'disputed').length
  const activeListings = listings.filter(l => l.status === 'active').length

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Not a seller yet — show onboarding CTA
  if (!sellerStatus?.is_seller) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-6xl mb-4">🏪</div>
          <h1 className="text-2xl font-bold text-white mb-3">Start Selling on Zenna</h1>
          <p className="text-slate-400 mb-6">Turn your craft designs into income. List patterns, SVGs, and digital designs for other crafters to buy.</p>
          <Link to="/marketplace" className="inline-block px-6 py-3 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white rounded-xl hover:opacity-90 transition">
            Go to Marketplace →
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Nav */}
      <nav className="border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center">
            <span className="text-white font-bold text-sm">Z</span>
          </div>
          <span className="text-white font-semibold">Zenna</span>
        </Link>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/dashboard" className="text-slate-400 hover:text-white transition">Dashboard</Link>
          <Link to="/marketplace" className="text-slate-400 hover:text-white transition">Shop</Link>
          <Link to="/orders" className="text-slate-400 hover:text-white transition">My Orders</Link>
          <button onClick={handleStripeDashboard} className="text-zenna-cyan hover:text-white transition">Stripe Dashboard →</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Seller Dashboard</h1>
            <p className="text-sm text-slate-500">Manage your listings, orders, and payouts</p>
          </div>
          <Link to="/marketplace" className="px-4 py-2 text-sm bg-zenna-violet text-white rounded-lg hover:bg-zenna-violet/80 transition">
            + New Listing
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Total Revenue</div>
            <div className="text-xl font-bold text-emerald-400">${totalRevenue.toFixed(2)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Active Listings</div>
            <div className="text-xl font-bold text-white">{activeListings}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Pending Orders</div>
            <div className="text-xl font-bold text-amber-400">{pendingOrders}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Disputes</div>
            <div className="text-xl font-bold text-red-400">{disputedOrders}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {['overview', 'orders', 'listings'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-lg capitalize transition ${
                tab === t ? 'bg-zenna-violet text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <h3 className="font-semibold text-white mb-3">Recent Orders</h3>
              {orders.length === 0 ? (
                <p className="text-sm text-slate-500">No orders yet. Share your listings to get your first sale!</p>
              ) : (
                <div className="space-y-2">
                  {orders.slice(0, 5).map(order => (
                    <div key={order.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/50 transition">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-white">{order.listing_snapshot?.title || 'Order'}</span>
                        <span className="ml-2 text-xs text-slate-500">#{order.order_number}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[order.status] || 'bg-slate-600 text-slate-300'}`}>
                        {order.status}
                      </span>
                      <span className="text-sm text-emerald-400 font-medium">${(order.seller_payout_amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders */}
        {tab === 'orders' && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No orders received yet</div>
            ) : orders.map(order => (
              <div key={order.id} className="bg-slate-800 rounded-xl border border-slate-700 p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-white">{order.listing_snapshot?.title || 'Order'}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[order.status] || ''}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    #{order.order_number} · {order.buyer_email} · {new Date(order.created_at).toLocaleDateString()}
                    {order.item_type === 'physical' && order.shipping_address && (
                      <span className="ml-2">📦 Ships to {order.shipping_address.state}</span>
                    )}
                  </div>
                </div>
                <span className="text-sm text-emerald-400 font-semibold">${(order.seller_payout_amount || 0).toFixed(2)}</span>

                {/* Ship button for physical, paid orders */}
                {order.item_type === 'physical' && ['paid', 'processing'].includes(order.status) && (
                  <button
                    onClick={() => handleShip(order.id)}
                    className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                  >
                    Mark Shipped
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Listings */}
        {tab === 'listings' && (
          <div className="space-y-3">
            {listings.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 mb-3">No listings yet</p>
                <Link to="/marketplace" className="text-zenna-violet hover:underline text-sm">Create your first listing →</Link>
              </div>
            ) : listings.map(listing => (
              <div key={listing.id} className="bg-slate-800 rounded-xl border border-slate-700 p-4 flex items-center gap-4">
                {listing.thumbnail_url ? (
                  <img src={listing.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center text-xl shrink-0">🎨</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{listing.title}</div>
                  <div className="text-xs text-slate-500">
                    {listing.category} · ${listing.price_personal} personal
                    {listing.purchase_count > 0 && <span className="ml-2 text-emerald-400">{listing.purchase_count} sales</span>}
                    {listing.rating_count > 0 && <span className="ml-2">⭐ {listing.rating_avg?.toFixed(1)}</span>}
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${listing.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-600 text-slate-400'}`}>
                  {listing.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
